import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, exportCsv, formatApiError, fmtQty, todayStr, monthStartStr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowDownToLine, Download, Trash2, Pencil, Flame, ArrowLeftRight, Workflow, ClipboardCheck } from "lucide-react";
import { StockFilters, cleanParams } from "@/components/stock/StockFilters";
import { TxnDialog } from "@/components/stock/TxnDialog";
import { TransferDialog } from "@/components/stock/TransferDialog";
import { ConversionDialog } from "@/components/stock/ConversionDialog";
import { AdjustmentDialog } from "@/components/stock/AdjustmentDialog";
import { DocEditDialog } from "@/components/stock/DocEditDialog";
import { POReceiptEditDialog, ReceiptDeleteDialog } from "@/components/purchasing/POReceiptEditDialog";
import { BulkButtons } from "@/components/common/BulkUpload";
import { DataTable } from "@/components/common/DataTable";
import { InOutBadge, ItemCell } from "@/components/common/cells";
import { toast } from "sonner";

const EDITABLE = ["manual", "used", "transfer", "conversion", "adjust", "opname", "opening"];
const ACTIONS = [
  { k: "in", labelKey: "stock_in", icon: ArrowDownToLine, cls: "bg-emerald-700 hover:bg-emerald-800" },
  { k: "used", labelKey: "used", icon: Flame, cls: "bg-amber-600 hover:bg-amber-700" },
  { k: "transfer", labelKey: "transfer", icon: ArrowLeftRight, cls: "bg-sky-700 hover:bg-sky-800" },
  { k: "conversion", labelKey: "conversion", icon: Workflow, cls: "bg-violet-700 hover:bg-violet-800" },
  { k: "adjust", labelKey: "adjustment", icon: ClipboardCheck, cls: "bg-slate-900 hover:bg-slate-800" },
];
const ROLE_CLS = { input: "bg-red-50 text-red-700", output: "bg-emerald-50 text-emerald-700", reject: "bg-rose-100 text-rose-800" };

export const TransactionsTab = ({ items, reloadItems }) => {
  const { t } = useLang();
  const { can } = useAuth();
  const canEdit = can("stock", "edit");
  const canReceipt = canEdit || can("purchasing", "edit");
  const [receiptDoc, setReceiptDoc] = useState(null);
  const [receiptDel, setReceiptDel] = useState(null);
  const [filters, setFilters] = useState({ start: monthStartStr(), end: todayStr(), item_type: "", direction: "", warehouse: "", ref_type: "" });
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [editTx, setEditTx] = useState(null);

  const load = useCallback(() => {
    api.get("/stock-transactions", { params: cleanParams(filters) }).then((r) => setRows(r.data));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); reloadItems(); };
  const close = () => setDialog(null);

  const remove = async (tx) => {
    if (!window.confirm(`${t("delete_doc_confirm")} (${tx.doc_no})`)) return;
    try {
      await api.delete(`/stock-transactions/${tx.id}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const whLabel = (tx) => `${t(`wh_${tx.warehouse}`)}${tx.counterpart ? ` ${tx.type === "out" ? "→" : "←"} ${t(`wh_${tx.counterpart}`)}` : ""}`;
  const columns = useMemo(() => [
    { key: "date", label: t("date"), className: "font-mono text-xs whitespace-nowrap" },
    { key: "doc_no", label: t("doc_no"), className: "font-mono text-xs text-slate-500 whitespace-nowrap" },
    { key: "type", label: t("txn_type"), align: "center", value: (r) => r.type.toUpperCase(), render: (r) => <InOutBadge type={r.type} /> },
    {
      key: "warehouse", label: t("warehouse"), value: whLabel, render: (r) => (
        <div className="text-xs whitespace-nowrap">
          <span className={`font-medium ${r.warehouse === "reject" ? "text-rose-700" : ""}`}>{whLabel(r)}</span>
          {r.role && <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] uppercase ${ROLE_CLS[r.role]}`}>{t(`role_${r.role}`)}</span>}
        </div>
      ),
    },
    { key: "item", label: t("item"), value: (r) => `${r.item_code} ${r.item_name}`, render: (r) => <ItemCell code={r.item_code} name={r.item_name} prefix={r.item_type.toUpperCase()} /> },
    {
      key: "qty", label: t("qty"), align: "right", value: (r) => (r.type === "in" ? r.qty : -r.qty), render: (r) => (
        <span className={`font-mono font-semibold whitespace-nowrap ${r.type === "in" ? "text-emerald-700" : "text-red-700"}`}>
          {r.type === "in" ? "+" : "−"}{fmtQty(r.qty)} <span className="text-xs text-slate-400">{r.unit}</span>
        </span>
      ),
    },
    { key: "source", label: t("source"), value: (r) => `${t(`src_${r.ref_type}`)} ${r.ref_no || ""}`, render: (r) => (
      <div className="text-xs"><div className="font-medium">{t(`src_${r.ref_type}`)}</div><div className="font-mono text-slate-500">{r.ref_no || "-"}</div></div>
    ) },
    { key: "note", label: t("note"), className: "text-xs text-slate-600", render: (r) => (
      <>{r.note || "-"}{r.ref_type === "opname" && <div className="text-slate-400">{t("system_qty")} {fmtQty(r.system_qty)} → {t("physical_qty")} {fmtQty(r.physical_qty)}</div>}</>
    ) },
    { key: "user", label: t("user"), className: "text-xs text-slate-500" },
    { key: "actions", label: "", filter: false, sort: false, render: (r) => (r.ref_type === "po" ? canReceipt && (
      <div className="flex gap-1 justify-end whitespace-nowrap">
        <Button variant="ghost" size="sm" onClick={() => setReceiptDoc(r.doc_no)} title={t("edit_receipt")} data-testid={`txn-edit-receipt-${r.id}`}><Pencil size={14} /></Button>
        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setReceiptDel(r)} title={t("delete")} data-testid={`txn-delete-receipt-${r.id}`}><Trash2 size={14} /></Button>
      </div>
    ) : canEdit && EDITABLE.includes(r.ref_type) && (
      <div className="flex gap-1 justify-end whitespace-nowrap">
        <Button variant="ghost" size="sm" onClick={() => setEditTx(r)} data-testid={`txn-edit-${r.id}`}><Pencil size={14} /></Button>
        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(r)} data-testid={`txn-delete-${r.id}`}><Trash2 size={14} /></Button>
      </div>
    )) },
  ], [t, canEdit, canReceipt, load]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        {canEdit && (
          <div className="p-4 border-b border-slate-200 flex flex-wrap gap-2" data-testid="stock-actions">
            {ACTIONS.map((a) => (
              <Button key={a.k} size="sm" className={a.cls} onClick={() => setDialog(a.k)} data-testid={`stock-${a.k}-btn`}>
                <a.icon size={14} className="mr-2" />{t(a.labelKey)}
              </Button>
            ))}
            <div className="flex gap-2 ml-auto">
              <BulkButtons kind="opening-stock" title={t("opening_stock")} onDone={refresh} />
            </div>
          </div>
        )}
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-4 items-end justify-between">
          <StockFilters value={filters} onChange={setFilters} showDirection showWarehouse showCategory testid="txn-filter" />
          <Button size="sm" variant="outline" onClick={() => exportCsv("stock-transactions", cleanParams(filters))} data-testid="txn-export-btn">
            <Download size={14} className="mr-2" />{t("export_excel")}
          </Button>
        </div>
        <DataTable columns={columns} rows={rows} testid="stock-transactions-table" />
      </CardContent>
      <TxnDialog type={["in", "out", "used"].includes(dialog) ? dialog : null} onClose={close} items={items} onSaved={refresh} />
      <TransferDialog open={dialog === "transfer"} onClose={close} items={items} onSaved={refresh} />
      <ConversionDialog open={dialog === "conversion"} onClose={close} items={items} onSaved={refresh} />
      <AdjustmentDialog open={dialog === "adjust"} onClose={close} items={items} onSaved={refresh} />
      <DocEditDialog tx={editTx} onClose={() => setEditTx(null)} items={items} onSaved={refresh} />
      <POReceiptEditDialog docNo={receiptDoc} onClose={() => setReceiptDoc(null)} onSaved={refresh} />
      <ReceiptDeleteDialog tx={receiptDel} onClose={() => setReceiptDel(null)} onDone={refresh} />
    </Card>
  );
};
